import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Sun, Moon, Heart, Menu, X } from 'lucide-react';
import '../styles/landing.css';
import { logSaasEvent } from '../utils/saasAnalytics';

const LandingLayout = () => {
    const location = useLocation();
    const [scrolled, setScrolled] = useState(false);
    const [isDark, setIsDark] = useState(() => {
        const saved = localStorage.getItem('lp-theme');
        return saved ? saved === 'dark' : false;
    });
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [trialDays, setTrialDays] = useState(null);
    const [contactInfo, setContactInfo] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch Pricing
                const resPricing = await fetch('https://menu.searchisend.in/apimenu/landing_actions.php?action=fetch_pricing_settings');
                const dataPricing = await resPricing.json();
                if (dataPricing.success && dataPricing.pricing && dataPricing.pricing.trial_days !== undefined) {
                    setTrialDays(dataPricing.pricing.trial_days);
                }

                // Fetch Contact Settings
                const resContact = await fetch('https://menu.searchisend.in/apimenu/landing_actions.php?action=fetch_contact_settings');
                const dataContact = await resContact.json();
                if (dataContact.success && dataContact.settings) {
                    setContactInfo(dataContact.settings);
                }
            } catch (err) {
                console.error("Error fetching landing layout data:", err);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        logSaasEvent('page_view', location.pathname);
    }, [location.pathname]);

    const toggleTheme = () => {
        setIsDark(prev => {
            const next = !prev;
            localStorage.setItem('lp-theme', next ? 'dark' : 'light');
            return next;
        });
    };

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 60);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu when route changes
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    return (
        <div className={`landing-page ${isDark ? 'lp-dark' : 'lp-light'}`}>
            {/* ── 1. STICKY NAVIGATION ── */}
            <nav className={`landing-nav ${scrolled ? 'scrolled' : ''}`}>
                <Link to="/" className="brand">
                    <img src="https://menu.searchisend.in/logo512.png" alt="SIE QR Menu logo" style={{ width: '36px', borderRadius: '8px' }} />
                    <span>SIE QR Menu</span>
                </Link>
                <div className="nav-links">
                    <a href="/#features" className="nav-link">Features</a>
                    <a href="/#pricing" className="nav-link">Pricing</a>
                    <a href="/#faq" className="nav-link">FAQ</a>
                    <Link to="/contact" className="nav-link">Contact Us</Link>
                    <button onClick={toggleTheme} className="theme-toggle-btn" aria-label="Toggle theme">
                        {isDark ? <Sun size={18} /> : <Moon size={18} />}
                    </button>
                    <Link to="/login" className="nav-link">Log in</Link>
                    <Link to="/signup" className="nav-cta-btn">{trialDays && trialDays > 0 ? `Start ${trialDays}-Day Free Trial` : 'Get Started'}</Link>
                </div>
                {/* Mobile hamburger & theme */}
                <div className="nav-mobile-cta">
                    <button onClick={toggleTheme} className="theme-toggle-btn mobile-theme-icon" aria-label="Toggle theme" style={{ padding: '6px', border: 'none', background: 'transparent' }}>
                        {isDark ? <Sun size={24} /> : <Moon size={24} />}
                    </button>
                    <button className="hamburger-btn" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
                        {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                    </button>
                </div>
            </nav>

            {/* ── MOBILE DRAWER OVERLAY ── */}
            <div className={`mobile-drawer-overlay ${isMobileMenuOpen ? 'open' : ''}`} onClick={() => setIsMobileMenuOpen(false)}></div>
            <div className={`mobile-drawer ${isMobileMenuOpen ? 'open' : ''}`}>
                <div className="mobile-drawer-header">
                    <Link to="/" className="brand" onClick={() => setIsMobileMenuOpen(false)}>
                        <img src="https://menu.searchisend.in/logo512.png" alt="SIE QR Menu logo" style={{ width: '36px', borderRadius: '8px' }} />
                        <span>SIE QR Menu</span>
                    </Link>
                    <button className="drawer-close-btn" onClick={() => setIsMobileMenuOpen(false)}><X size={24} /></button>
                </div>
                <div className="mobile-drawer-links">
                    <a href="/#features" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>Features</a>
                    <a href="/#pricing" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>Pricing</a>
                    <a href="/#faq" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>FAQ</a>
                    <Link to="/contact" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>Contact Us</Link>
                </div>
                <div className="mobile-drawer-footer">
                    <Link to="/login" className="premium-btn-secondary" style={{ width: '100%', textAlign: 'center', margin: '0 auto 12px auto' }} onClick={() => setIsMobileMenuOpen(false)}>Log In</Link>
                    <Link to="/signup" className="premium-btn-primary" style={{ width: '100%', textAlign: 'center', margin: '0 auto' }} onClick={() => setIsMobileMenuOpen(false)}>{trialDays && trialDays > 0 ? `Start ${trialDays}-Day Free Trial` : 'Get Started'}</Link>
                </div>
            </div>

            {/* This will render the children (LandingPage, Login, Wizard) */}
            <Outlet />

            {/* ── FOOTER ── */}
            <footer className="landing-footer">
                <div className="footer-top">
                    <div className="footer-brand-col">
                        <Link to="/" className="brand">
                            <img src="https://menu.searchisend.in/logo512.png" alt="SIE QR Menu logo" style={{ width: '36px', borderRadius: '8px' }} />
                            <span>SIE QR Menu</span>
                        </Link>
                        <p>The #1 digital QR menu platform built exclusively for restaurants in India.</p>
                        <div className="footer-social" style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                            {contactInfo?.contact_facebook && (
                                <a href={contactInfo.contact_facebook} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px' }}>
                                    <i className="fab fa-facebook-f" style={{ fontSize: '1.1rem' }}></i>
                                </a>
                            )}
                            {contactInfo?.contact_youtube && (
                                <a href={contactInfo.contact_youtube} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px' }}>
                                    <i className="fab fa-youtube" style={{ fontSize: '1.1rem' }}></i>
                                </a>
                            )}
                            {contactInfo?.contact_instagram && (
                                <a href={contactInfo.contact_instagram} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px' }}>
                                    <i className="fab fa-instagram" style={{ fontSize: '1.1rem' }}></i>
                                </a>
                            )}
                            {contactInfo?.contact_phone && (
                                <a href={`https://wa.me/${contactInfo.contact_phone.replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', background: 'rgba(255,255,255,0.05)', padding: '10px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px' }}>
                                    <i className="fab fa-whatsapp" style={{ fontSize: '1.1rem' }}></i>
                                </a>
                            )}
                        </div>
                    </div>
                    <div className="footer-links-col">
                        <h4>Product</h4>
                        <a href="/#features">Features</a>
                        <a href="/#pricing">Pricing</a>
                        <a href="https://menu.searchisend.in/demo-kitchen" target="_blank" rel="noopener noreferrer">Live Demo</a>
                    </div>
                    <div className="footer-links-col">
                        <h4>Company</h4>
                        <Link to="/contact">Contact Us</Link>
                        <a href="/#faq">FAQ</a>
                    </div>
                    <div className="footer-links-col">
                        <h4>Legal</h4>
                        <Link to="/privacy">Privacy Policy</Link>
                        <Link to="/terms">Terms of Service</Link>
                        <Link to="/refund-policy">Refund Policy</Link>
                    </div>
                </div>
                <div className="footer-bottom">
                    <p>© {new Date().getFullYear()} Smart City Ajmer operating as Search is End! All rights reserved.</p>
                    <p>Made with <Heart size={14} fill="#ef4444" color="#ef4444" style={{ display: 'inline', verticalAlign: 'middle', margin: '0 4px' }} /> for Indian Restaurants</p>
                </div>
            </footer>
        </div>
    );
};

export default LandingLayout;
